import React, { useState, useEffect, useRef } from "react";
import {
    Modal,
    Table,
    Button,
    Input,
    Row,
    Col,
    Typography,
    Space,
    message,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { postRequest } from "../../services/mainApp.service";
import ModalComponent from '../../components/formComponent/ModalComponent';
import SalesReturnSlip from '../../components/NewComponents/SalesReturnSlip';

const SalesReturnModal = (props) => {

    const { open, onClose, selectedOrder, onSuccess } = props;

    const userData = useSelector((state) => state.authReducer);
    const { userBranchList } = useSelector((state) => state.authReducer);

    const [saleReturnOrderData, setSaleReturnOrderData] = useState(null);
    const [saleReturnOrderItems, setSaleReturnOrderItems] = useState([]);
    const [saleReturnLoading, setSaleReturnLoading] = useState(false);
    const [salesReturnResponse, setSalesReturnResponse] = useState(null);
    const [returnedItemsForPrint, setReturnedItemsForPrint] = useState([]);

    const salesReturnSlipRef = useRef();

    // Function to organize order items (parent with children)
    const organizeOrderItems = (items) => {
        // First get all parent items (DealItemId is null)
        const parentItems = items.filter((item) => item.DealItemId === null);

        const result = [];

        for (const parent of parentItems) {
            // Find children - same RandomId AND DealItemId is NOT null
            const children = items.filter(
                (item) => item.RandomId === parent.RandomId && item.DealItemId !== null
            );

            result.push({
                parent,
                children,
            });
        }

        return result;
    };

    useEffect(() => {
        if (open && selectedOrder?.OrderMasterId) {
            fetchOrderDetails();
        } else if (!open) {
            // Reset state when modal closes
            setSaleReturnOrderData(null);
            setSaleReturnOrderItems([]);
        }
    }, [open, selectedOrder]);

    const fetchOrderDetails = async () => {
        if (!selectedOrder?.OrderMasterId) {
            message.warning("Please select an order for sales return.");
            return;
        }

        setSaleReturnLoading(true);

        try {
            // First, get the order details
            const response = await postRequest("getOrderDetail", {
                OperationId: 1,
                CompanyId: userData.CompanyId,
                OrderId: selectedOrder.OrderMasterId,
                UserId: userData.UserId,
                UserIP: "192.1.1.1",
            });

            const dataSet = response?.data?.DataSet;

            if (!dataSet) {
                throw new Error("Empty order detail dataset");
            }

            // Set order master data
            setSaleReturnOrderData(dataSet.Table?.[0] || selectedOrder);

            // Get order items from Table1 and initialize with SaleReturnQty as 0
            const orderItems = dataSet.Table1 || [];

            // Initialize items with SaleReturnQty, ReturnQuantity, and ReturnAmount as 0
            const itemsWithReturnQty = orderItems.map(item => ({
                ...item,
                SaleReturnQty: 0,
                ReturnQuantity: 0,
                ReturnAmount: 0
            }));

            setSaleReturnOrderItems(itemsWithReturnQty);
        } catch (error) {
            console.error("Sales return error:", error);
            message.error("Unable to load order details for sales return. Please try again.");
            onClose();
        } finally {
            setSaleReturnLoading(false);
        }
    };

    const handleSaleReturnQtyChange = (orderDetailId, value) => {
        const numValue = parseFloat(value) || 0;
        setSaleReturnOrderItems(prevItems =>
            prevItems.map(item => {
                if (item.OrderDetailId === orderDetailId) {
                    // Get the price field (prefer PriceWithGST, fallback to Price or PriceWithoutGST)
                    const itemPrice = item.PriceWithGST || item.Price || item.PriceWithoutGST || 0;
                    // Calculate ReturnAmount as ReturnQuantity * Price
                    const returnAmount = numValue * itemPrice;
                    return {
                        ...item,
                        SaleReturnQty: numValue,
                        ReturnQuantity: numValue,
                        ReturnAmount: returnAmount
                    };
                }
                return item;
            })
        );
    };

    const handleReturnWholeOrder = () => {
        setSaleReturnOrderItems(prevItems =>
            prevItems.map(item => {
                // Only set return quantities for parent items (DealItemId === null)
                if (item.DealItemId === null) {
                    const quantity = parseFloat(item.Quantity) || 0;
                    // Get the price field (prefer PriceWithGST, fallback to Price or PriceWithoutGST)
                    const itemPrice = item.PriceWithGST || item.Price || item.PriceWithoutGST || 0;
                    // Calculate ReturnAmount as ReturnQuantity * Price
                    const returnAmount = quantity * itemPrice;
                    return {
                        ...item,
                        SaleReturnQty: quantity,
                        ReturnQuantity: quantity,
                        ReturnAmount: returnAmount
                    };
                }
                return item;
            })
        );
        message.success("All items set for full return");
    };

    // Get organized items for display
    const filteredData = organizeOrderItems(saleReturnOrderItems || []);

    const handleSaveSaleReturn = async () => {
        // Validate that at least one item has return quantity > 0
        const hasReturnQty = saleReturnOrderItems.some(item => item.SaleReturnQty > 0);

        if (!hasReturnQty) {
            message.error("Please enter return quantity for at least one item.");
            return;
        }

        // Filter items with return quantity > 0
        // ReturnQuantity and ReturnAmount are already set in handleSaleReturnQtyChange
        const salesReturnDetail = saleReturnOrderItems
            .filter(item => item.SaleReturnQty > 0 && item.ReturnQuantity > 0)
            .map(item => ({
                ...item,
                // Ensure ReturnQuantity and ReturnAmount are set (they should already be set)
                ReturnQuantity: item.ReturnQuantity || item.SaleReturnQty || 0,
                ReturnAmount: item.ReturnAmount || 0
            }));

        setSaleReturnLoading(true);

        try {
            const month =
                new Date().getMonth() + 1 < 10
                    ? "0" + (new Date().getMonth() + 1)
                    : new Date().getMonth() + 1;
            const day =
                new Date().getDate() < 10
                    ? "0" + new Date().getDate()
                    : new Date().getDate();

            const response = await postRequest("/CrudSaleReturn", {
                OperationId: 2,
                SalesReturnId: null,
                SalesReturnNumber: null,
                UserId: userData.UserId,
                Date: new Date().getFullYear() + "-" + month + "-" + day,
                BranchId: userData.branchId || userData.userBranchList?.[0]?.BranchId,
                UserIP: "1.0.0.1",
                OrderMasterId: saleReturnOrderData?.OrderMasterId || selectedOrder?.OrderMasterId,
                NetAmount: null,
                CompanyId: userData.CompanyId,
                SalesReturnDetail: salesReturnDetail,
                DiscountPercent: saleReturnOrderData?.DiscountPercent || 0,
            });

            if (response.error === true) {
                message.error(response.errorMessage);
                return;
            }

            message.success("Sales return saved successfully!");

            // Store response data and returned items for printing
            const responseData = response.data?.DataSet?.Table?.[0] || response.data?.DataSet?.Table1?.[0] || {};
            setSalesReturnResponse({
                ...saleReturnOrderData,
                ...responseData,
                SalesReturnNumber: responseData.SalesReturnNumber || responseData.SalesReturnId || 'N/A',
                ReturnDate: new Date(),
                branchName: saleReturnOrderData?.BranchName || userData?.branchName,
                AreaName: saleReturnOrderData?.AreaName
            });
            setReturnedItemsForPrint(salesReturnDetail);

            // Trigger print after a short delay
            setTimeout(() => {
                if (salesReturnSlipRef.current) {
                    salesReturnSlipRef.current.print();
                }
            }, 500);

            // Reset state after print
            setTimeout(() => {
                setSaleReturnOrderData(null);
                setSaleReturnOrderItems([]);
                setSalesReturnResponse(null);
                setReturnedItemsForPrint([]);

                // Close modal and trigger refresh
                onClose();
                if (onSuccess) {
                    onSuccess();
                }
            }, 1000);
        } catch (error) {
            console.error("Save sales return error:", error);
            message.error("Unable to save sales return. Please try again.");
        } finally {
            setSaleReturnLoading(false);
        }
    };

    const handleCancel = () => {
        setSaleReturnOrderData(null);
        setSaleReturnOrderItems([]);
        setSalesReturnResponse(null);
        setReturnedItemsForPrint([]);
        onClose();
    };

    return (
        // <ModalComponent
        //     title="Sales Return"
        //     open={open}
        //     onCancel={handleCancel}
        //     footer={null}
        //     width={900}
        // >

        <ModalComponent
            isModalVisible={open}
            title="Sales Return"
            handleCancel={handleCancel}
            okText={'Save'}
            width={"80vw"}
            footer={null}>

            {saleReturnLoading && !saleReturnOrderData ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <LoadingOutlined style={{ fontSize: 24 }} />
                    <p>Loading order details...</p>
                </div>
            ) : (
                <>
                    {/* Order Details Section */}
                    <div style={{ marginBottom: '20px' }}>
                        <Typography.Title level={4}>Order Details</Typography.Title>
                        <Row gutter={[16, 8]}>
                            <Col span={12}>
                                <Typography.Text strong>Order Number: </Typography.Text>
                                <Typography.Text>{saleReturnOrderData?.OrderNumber || '-'}</Typography.Text>
                            </Col>
                            <Col span={12}>
                                <Typography.Text strong>Order Mode: </Typography.Text>
                                <Typography.Text>{saleReturnOrderData?.OrderMode || saleReturnOrderData?.OrderModeName || '-'}</Typography.Text>
                            </Col>
                            <Col span={12}>
                                <Typography.Text strong>Order Source: </Typography.Text>
                                <Typography.Text>{saleReturnOrderData?.OrderSource || saleReturnOrderData?.OrderSourceValue || '-'}</Typography.Text>
                            </Col>
                            <Col span={12}>
                                <Typography.Text strong>Order Date Time: </Typography.Text>
                                <Typography.Text>{saleReturnOrderData?.OrderDateTime || saleReturnOrderData?.OrderDate || '-'}</Typography.Text>
                            </Col>
                            {saleReturnOrderData?.TableName && (
                                <Col span={12}>
                                    <Typography.Text strong>Table: </Typography.Text>
                                    <Typography.Text>{saleReturnOrderData.TableName}</Typography.Text>
                                </Col>
                            )}
                            {saleReturnOrderData?.WaiterName && (
                                <Col span={12}>
                                    <Typography.Text strong>Waiter: </Typography.Text>
                                    <Typography.Text>{saleReturnOrderData.WaiterName}</Typography.Text>
                                </Col>
                            )}
                            {saleReturnOrderData?.RiderName && (
                                <Col span={12}>
                                    <Typography.Text strong>Rider: </Typography.Text>
                                    <Typography.Text>{saleReturnOrderData.RiderName}</Typography.Text>
                                </Col>
                            )}
                        </Row>


                    </div>

                    {/* Order Items Display */}
                    <div style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                marginRight: '8px'
                            }}>
                                <span style={{
                                    fontSize: '12px',
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    padding: '2px 8px',
                                    borderRadius: '9999px'
                                }}>
                                    {filteredData.length}
                                </span>
                                <Typography.Title level={4} style={{ margin: 0 }}>
                                    Order Items
                                </Typography.Title>
                            </div>
                            <Button
                                type="default"
                                onClick={handleReturnWholeOrder}
                                style={{
                                    backgroundColor: '#fef3c7',
                                    borderColor: '#f59e0b',
                                    color: '#92400e'
                                }}
                            >
                                Return Complete Order
                            </Button>
                        </div>

                        <div style={{
                            maxHeight: '380px',
                            overflowY: 'auto',
                            paddingRight: '8px'
                        }}>
                            {filteredData?.map((group, groupIndex) => (
                                <div
                                    key={groupIndex}
                                    style={{
                                        padding: '8px',
                                        backgroundColor: '#f0fdf4',
                                        borderRadius: '6px',
                                        marginBottom: '8px'
                                    }}
                                >
                                    {/* Parent Item */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '8px'
                                    }}>
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '4px'
                                            }}>
                                                <div style={{
                                                    fontSize: '16px',
                                                    fontWeight: 600,
                                                    color: '#15803d'
                                                }}>
                                                    {group.parent.ProductName}{" "}
                                                    {group.parent.SizeName}{" "}
                                                    {group.parent.FlavourName}
                                                </div>
                                                <div style={{
                                                    fontSize: '16px',
                                                    color: '#15803d',
                                                    textAlign: 'right',
                                                    minWidth: '80px'
                                                }}>
                                                    Rs.{" "}
                                                    {group.parent.PriceWithGST?.toLocaleString()}
                                                </div>
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                                fontSize: '14px',
                                                color: '#15803d',
                                                gap: '8px'
                                            }}>
                                                {group.parent.RemoteId && (
                                                    <span>POS: {group.parent.RemoteId}</span>
                                                )}
                                                <span>•</span>
                                                <span>Quantity: {group.parent.Quantity}</span>
                                                {group.parent.DiscountPercent > 0 && (
                                                    <>
                                                        <span>•</span>
                                                        <span style={{ color: '#e11d48' }}>
                                                            {group.parent.DiscountPercent}% OFF
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                            {/* Sale Return Qty Input for Parent */}
                                            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ fontSize: '14px', fontWeight: 500 }}>Sale Return Qty:</span>
                                                <Input
                                                    type="number"
                                                    min={0}
                                                    max={group.parent.Quantity || 0}
                                                    value={group.parent.SaleReturnQty || 0}
                                                    onChange={(e) => {
                                                        const value = parseFloat(e.target.value) || 0;
                                                        const maxQty = group.parent.Quantity || 0;
                                                        handleSaleReturnQtyChange(
                                                            group.parent.OrderDetailId,
                                                            value > maxQty ? maxQty : value
                                                        );
                                                    }}
                                                    style={{ width: '100px' }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Child Items (if any) */}
                                    {group.children?.length > 0 && (
                                        <div style={{ marginTop: '8px', marginLeft: '32px' }}>
                                            {group.children.map((child, childIndex) => (
                                                <div
                                                    key={childIndex}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                        fontSize: '14px',
                                                        color: '#374151',
                                                        marginBottom: '8px'
                                                    }}
                                                >
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            marginBottom: '4px'
                                                        }}>
                                                            <div style={{ fontWeight: 500 }}>
                                                                {child.ProductName} {child.SizeName}{" "}
                                                                {child.FlavourName}
                                                            </div>
                                                            <div style={{ textAlign: 'right', minWidth: '60px' }}>
                                                                + Rs.{" "}
                                                                {child.PriceWithGST?.toLocaleString()}
                                                            </div>
                                                        </div>
                                                        <div style={{
                                                            fontSize: '12px',
                                                            color: '#6b7280',
                                                            display: 'flex',
                                                            gap: '4px',
                                                            flexWrap: 'wrap'
                                                        }}>
                                                            {child.RemoteId && (
                                                                <span>
                                                                    POS: {child.RemoteId} &nbsp; Qty:{" "}
                                                                    {child.Quantity}
                                                                </span>
                                                            )}
                                                            {child.DiscountPercent > 0 && (
                                                                <span style={{ color: '#e11d48' }}>
                                                                    {child.DiscountPercent}% OFF
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Payment Summary Section */}
                    <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                        <Row gutter={[16, 8]}>
                            <Col span={3}>
                                <Typography.Text strong>Subtotal: </Typography.Text>
                                <Typography.Text>Rs. {parseFloat(saleReturnOrderData?.TotalAmountWithoutGST || saleReturnOrderData?.Gross || 0).toFixed(2)}</Typography.Text>
                            </Col>
                            <Col span={3}>
                                <Typography.Text strong>Tax: </Typography.Text>
                                <Typography.Text>Rs. {parseFloat(saleReturnOrderData?.TotalGST || saleReturnOrderData?.GSTAmount || saleReturnOrderData?.Tax || 0).toFixed(2)}</Typography.Text>
                            </Col>
                            <Col span={3}>
                                <Typography.Text strong>Discount: </Typography.Text>
                                <Typography.Text style={{ color: '#e11d48' }}>
                                    -Rs. {parseFloat(saleReturnOrderData?.DiscountAmount || saleReturnOrderData?.Discount || 0).toFixed(2)}
                                </Typography.Text>
                            </Col>
                            {saleReturnOrderData?.DeliveryCharges > 0 && (
                                <Col span={3}>
                                    <Typography.Text strong>Delivery Charges: </Typography.Text>
                                    <Typography.Text>Rs. {parseFloat(saleReturnOrderData.DeliveryCharges || 0).toFixed(2)}</Typography.Text>
                                </Col>
                            )}
                            <Col span={3}>

                                <Typography.Text strong style={{ fontSize: '16px' }}>Net Bill: </Typography.Text>
                                <Typography.Text strong style={{ fontSize: '18px', color: '#15803d' }}>
                                    Rs. {parseFloat(saleReturnOrderData?.TotalAmountWithGST || saleReturnOrderData?.NetAmount || 0).toFixed(2)}
                                </Typography.Text>

                            </Col>
                        </Row>
                    </div>

                    {/* Save Button */}
                    <div style={{ textAlign: 'right', marginTop: '20px' }}>
                        <Space>
                            <Button onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleSaveSaleReturn}
                                loading={saleReturnLoading}
                            >
                                Save Sale Return
                            </Button>
                        </Space>
                    </div>
                </>
            )}
            {/* Sales Return Slip Print Component */}
            <SalesReturnSlip
                ref={salesReturnSlipRef}
                orderDetails={salesReturnResponse}
                orderItems={saleReturnOrderItems}
                returnedItems={returnedItemsForPrint}
                heading="SALE RETURN SLIP"
            />
        </ModalComponent>
    );
};

export default SalesReturnModal;

